/**
 * La nota pubblica di un obbligo senza contenuti: perche' il file non c'e'.
 *
 * Esiste perche' un obbligo vuoto, sul portale, diceva soltanto «non risultano
 * documenti pubblicati», e il cittadino non poteva distinguere un ritardo da
 * una dimenticanza. Con la nota l'amministrazione lo spiega — «in attesa
 * dell'approvazione del rendiconto» — finche' il contenuto non arriva.
 *
 * Compare sul portale **subito**, senza «Pubblica», e solo finche' l'obbligo
 * non ha contenuti pubblicati: la pubblicazione del primo la cancella, e se
 * quel contenuto va poi in archivio la nota non torna, va riscritta. Su un
 * obbligo che ha gia' contenuti pubblicati l'API la rifiuta. Lo si dice nella
 * modale, insieme all'avvertenza sui dati personali: e' testo libero su una
 * pagina pubblica.
 */

import { useState } from "react";
import type { ReactNode } from "react";
import { ErroreApi, impostaNotaAssenza } from "../api/client";
import type { IdNodo, Nodo } from "../api/types";
import { leggiNotaAssenza, NOTA_ASSENZA_MASSIMO } from "../lib/allerte";
import { CampoLibero } from "./campi";
import { Modale } from "./Modale";
import { Avviso, Bottone } from "./primitive";

export function ModaleNotaAssenza({
  idNodo,
  denominazione,
  notaAttuale,
  enteId,
  contesto,
  onChiudi,
  onCompletato,
}: {
  idNodo: IdNodo;
  denominazione: string;
  notaAttuale?: string;
  enteId: number | null;
  contesto?: ReactNode;
  onChiudi: () => void;
  onCompletato: (aggiornato: Nodo) => void;
}) {
  const iniziale = notaAttuale ?? "";
  const [testo, setTesto] = useState(iniziale);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const letta = leggiNotaAssenza(testo);
  const invariata = testo.trim() === iniziale.trim();

  const salva = async () => {
    if (letta.errore || invariata) return;
    setErrore(null);
    setInCorso(true);
    try {
      onCompletato(await impostaNotaAssenza(idNodo, letta.testo, enteId));
    } catch (causa) {
      setErrore(causa instanceof ErroreApi ? causa.messaggioEsteso : "Commento non salvato.");
    } finally {
      setInCorso(false);
    }
  };

  const chiudi = () => {
    if (!inCorso) onChiudi();
  };

  return (
    <Modale
      titolo={`Commento pubblico su «${denominazione}»`}
      aperta
      onChiudi={chiudi}
      pedice={
        <>
          <Bottone variante="fantasma" onClick={chiudi} disabled={inCorso}>
            Annulla
          </Bottone>
          <Bottone
            variante="primario"
            inCorso={inCorso}
            disabled={Boolean(letta.errore) || invariata}
            onClick={() => void salva()}
          >
            {letta.testo === null && iniziale ? "Togli il commento" : "Salva e mostra sul portale"}
          </Bottone>
        </>
      }
    >
      <div className="pila">
        {contesto ? <div>{contesto}</div> : null}
        {errore ? (
          <Avviso tono="critico" titolo="Commento non salvato">
            {errore}
          </Avviso>
        ) : null}
        <CampoLibero
          etichetta="Perche' il documento non e' disponibile"
          valore={testo}
          onCambia={setTesto}
          multilinea
          errore={letta.errore}
          aiuto={`${testo.trim().length}/${NOTA_ASSENZA_MASSIMO} caratteri. Vuoto: il commento si toglie.`}
        />
        <Avviso tono="attenzione">
          Compare subito sulla pagina pubblica dell'obbligo, finche' non viene pubblicato un
          contenuto. Non inserire dati personali.
        </Avviso>
      </div>
    </Modale>
  );
}
